import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { Modal } from '../../../../components'
import { useOptions } from '../../../../providers/Options/Options.provider'
import { usePeerGroups } from '../../../../providers/PeerGroups/PeerGroups.provider'
import {
  PeerGroupProvider,
  PeerGroupSession,
} from '../../../../providers/PeerGroups/types'

interface AddSessionProvidersModalProps {
  setShowAddSessionProvidersModal: React.Dispatch<React.SetStateAction<boolean>>
  showAddSessionProvidersModal: boolean
}

export const AddSessionProvidersModal = ({
  showAddSessionProvidersModal,
  setShowAddSessionProvidersModal,
}: AddSessionProvidersModalProps) => {
  const {
    peerGroupDetails,
    addRemoveProvidersSinglePeerGroupSession,
    getPeerGroupDetails,
  } = usePeerGroups()

  const [selectedSessionId, setSelectedSessionId] = useState('')
  const [selectedSession, setSelectedSession] = useState<PeerGroupSession>({
    id: 0,
    start: '',
    end: '',
    mediaIds: [],
    moderatorObservers: [],
  })

  const [selectedProviders, setSelectedProviders] = useState<
    PeerGroupProvider[]
  >([])

  const [selectedProviderId, setSelectedProviderId] = useState<number>(0)

  const { options } = useOptions()

  const handleAddProvider = (providerId: number) => {
    setSelectedProviders((prevPeerGroupProviders: PeerGroupProvider[]) => {
      if (prevPeerGroupProviders.every((p) => p.providerId !== providerId)) {
        const providerOption = options.providers.find(
          (p) => p.providerId === providerId
        )
        const name = providerOption.label.replace(/\s*\(.*?\)/, '').trim()
        const newPGProvider = {
          providerId,
          isModerator: false,
          isCoModerator: false,
          isObserver: false,
          displayName: name,
        }
        return [...prevPeerGroupProviders, newPGProvider]
      } else {
        return [...prevPeerGroupProviders]
      }
    })
  }

  const handleRemoveProvider = (providerId: number) => {
    console.log('selectedProviderId: ', providerId)
    setSelectedProviders((prevProviders) => {
      return prevProviders.filter((p) => p.providerId !== providerId)
    })
  }

  const handleRoleCheckboxChange = (
    providerId: number,
    roleProperty: string,
    checked: boolean
  ) => {
    setSelectedProviders((prevProviders) =>
      prevProviders.map((provider) => {
        if (provider.providerId !== providerId) return provider

        return {
          ...provider,
          isModerator: false,
          isCoModerator: false,
          isObserver: false,
          [roleProperty]: checked,
        }
      })
    )
  }

  useEffect(() => {
    if (peerGroupDetails && peerGroupDetails.sessions.length > 0) {
      setSelectedSessionId(String(peerGroupDetails?.sessions[0].id))
    }
  }, [peerGroupDetails])

  useEffect(() => {
    if (options.providers !== null) {
      setSelectedProviderId(options.providers[0].providerId)
    }
  }, [options.providers])

  useEffect(() => {
    const matchingSession = peerGroupDetails?.sessions.find(
      (session) => session.id === Number(selectedSessionId)
    )
    if (matchingSession) {
      setSelectedSession(matchingSession)
    }
  }, [selectedSessionId])

  useEffect(() => {
    setSelectedProviders(selectedSession.moderatorObservers ?? [])
  }, [selectedSession])

  const handleSave = async () => {
    await addRemoveProvidersSinglePeerGroupSession({
      sessionId: parseInt(selectedSessionId),
      moderatorObservers: selectedProviders,
    })
    await getPeerGroupDetails(peerGroupDetails?.peerGroupId)
    setShowAddSessionProvidersModal(false)
  }

  return (
    <Modal
      open={showAddSessionProvidersModal}
      setOpen={setShowAddSessionProvidersModal}
      title={'ADD MODERATORS, CO-MODERATORS, & OBSERVERS TO SESSIONS'}
      width='40%'
      ModalContent={
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'space-between',
            justifyContent: 'space-between',
          }}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 'max-content',
              alignSelf: 'center',
            }}
          >
            <h3 style={{ marginRight: '15px' }}>Session Date: </h3>
            <select onChange={(e) => setSelectedSessionId(e.target.value)}>
              {peerGroupDetails?.sessions?.map((session) => {
                return (
                  <option key={session.id} value={session.id}>
                    {dayjs(session.start).format('MM/DD/YYYY hh:mmA')}
                  </option>
                )
              })}
            </select>
          </Box>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 'max-content',
              alignSelf: 'center',
            }}
          >
            <h3 style={{ marginRight: '15px' }}>Provider: </h3>
            <select
              onChange={(e) => setSelectedProviderId(parseInt(e.target.value))}
            >
              {options.providers.map((provider) => {
                return (
                  <option key={provider.providerId} value={provider.providerId}>
                    {provider.label}
                  </option>
                )
              })}
            </select>
            <Button
              variant='contained'
              onClick={() => {
                handleAddProvider(selectedProviderId)
              }}
              style={{ width: '2px' }}
            >
              Add
            </Button>
          </Box>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignContent: 'center',
              height: '200px',
              overflowY: 'scroll',
              padding: '10px 0',
            }}
          >
            {selectedProviders.map((provider, i) => {
              return (
                <Box
                  key={`peer-group-provider-${i}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignContent: 'center',
                    marginBottom: '5px',
                    width: '50%',
                    minWidth: '350px',
                    alignSelf: 'center',
                  }}
                >
                  <IconButton
                    color='error'
                    aria-label='delete'
                    onClick={() => {
                      handleRemoveProvider(provider.providerId)
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <span
                    style={{
                      fontSize: '16px',
                      lineHeight: '20px',
                      marginBottom: '5px',
                      textAlign: 'left',
                      width: '300px',
                      padding: '0 10px',
                      wordWrap: 'break-word',
                    }}
                  >
                    {provider.displayName}
                  </span>
                  <span
                    style={{
                      fontSize: '16px',
                      lineHeight: '20px',
                      marginBottom: '5px',
                      textAlign: 'left',
                      width: '300px',
                      padding: '0 10px',
                      wordWrap: 'break-word',
                    }}
                  >
                    Moderator:
                  </span>
                  <input
                    type='checkbox'
                    checked={provider.isModerator ? true : false}
                    onChange={(e) => {
                      handleRoleCheckboxChange(
                        provider.providerId,
                        'isModerator',
                        e.target.checked
                      )
                    }}
                    className='form-checkbox h-5 w-5 text-blue-600'
                    style={{
                      width: '20px',
                      height: '20px',
                      margin: '0 10px',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '16px',
                      lineHeight: '20px',
                      marginBottom: '5px',
                      textAlign: 'left',
                      width: '300px',
                      padding: '0 10px',
                      wordWrap: 'break-word',
                    }}
                  >
                    Co-Moderator:
                  </span>
                  <input
                    type='checkbox'
                    checked={provider.isCoModerator ? true : false}
                    onChange={(e) => {
                      handleRoleCheckboxChange(
                        provider.providerId,
                        'isCoModerator',
                        e.target.checked
                      )
                    }}
                    className='form-checkbox h-5 w-5 text-blue-600'
                    style={{
                      width: '20px',
                      height: '20px',
                      margin: '0 10px',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '16px',
                      lineHeight: '20px',
                      marginBottom: '5px',
                      textAlign: 'left',
                      width: '300px',
                      padding: '0 10px',
                      wordWrap: 'break-word',
                    }}
                  >
                    Observer:
                  </span>
                  <input
                    type='checkbox'
                    checked={provider.isObserver ? true : false}
                    onChange={(e) => {
                      handleRoleCheckboxChange(
                        provider.providerId,
                        'isObserver',
                        e.target.checked
                      )
                    }}
                    className='form-checkbox h-5 w-5 text-blue-600'
                    style={{
                      width: '20px',
                      height: '20px',
                      margin: '0 10px',
                    }}
                  />
                </Box>
              )
            })}
          </div>
          <Button
            variant='contained'
            onClick={() => {
              handleSave()
            }}
            style={{ width: '50px', alignSelf: 'center', marginTop: '10px' }}
          >
            Save
          </Button>
        </Box>
      }
    />
  )
}
